import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/hooks/useLanguage";
import { CommandPalette } from "@/components/prometheus/CommandPalette";
import Index from "./pages/Index.tsx";
import ChatHomePage from "./pages/ChatHomePage.tsx";
import NotFound from "./pages/NotFound.tsx";
import MindsPage from "./pages/MindsPage.tsx";
import WorkshopPage from "./pages/WorkshopPage.tsx";
import ToolsPage from "./pages/ToolsPage.tsx";
import SecondBrainPage from "./pages/SecondBrainPage.tsx";

const ChatWidget = lazy(() =>
  import("@/components/prometheus/ChatWidget").then((m) => ({ default: m.ChatWidget }))
);

const queryClient = new QueryClient();

/** Floating chat only on light pages — skip OS home, Taller, Herramientas, Brain. */
function AppChatWidget() {
  const { pathname } = useLocation();
  if (
    pathname === "/" ||
    pathname === "" ||
    pathname === "/welcome" ||
    pathname.startsWith("/taller") ||
    pathname.startsWith("/herramientas") ||
    pathname.startsWith("/brain")
  ) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <ChatWidget />
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<ChatHomePage />} />
            <Route path="/welcome" element={<Index />} />
            <Route path="/landing" element={<Navigate to="/welcome" replace />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/mentes" element={<MindsPage />} />
            <Route path="/taller" element={<WorkshopPage />} />
            <Route path="/herramientas" element={<ToolsPage />} />
            <Route path="/herramientas/:tab" element={<ToolsPage />} />
            <Route path="/brain3d" element={<SecondBrainPage />} />
            <Route path="/brain" element={<Navigate to="/brain3d" replace />} />
            <Route path="/cerebro" element={<Navigate to="/brain3d" replace />} />
            <Route path="/vision" element={<Navigate to="/herramientas" replace />} />
            <Route path="/vision-lab" element={<Navigate to="/herramientas" replace />} />
            <Route path="/espacios" element={<Navigate to="/taller?station=dreamhouse" replace />} />
            <Route path="/arquitectura" element={<Navigate to="/taller?station=dreamhouse" replace />} />
            <Route path="/tunel-viento" element={<Navigate to="/taller?station=dreamhouse" replace />} />
            <Route path="/cfd" element={<Navigate to="/taller?station=dreamhouse" replace />} />
            <Route path="/aerodinamica" element={<Navigate to="/taller?station=dreamhouse" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <AppChatWidget />
          <CommandPalette />
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
